import { useState } from 'react';
import { Banner, Button, Card, Dialog, Field, PageHeader, Pill, type Tone } from '@elysian/ui';
import type { ChecklistItem, FiscalYear, Period } from '../../data/source';
import { useAsync } from '../../data/useAsync';
import { useSession } from '../../state/session';
import { useToast } from '../../state/toast';
import { longDate } from '../invoices/shared';

const PERIOD: Record<Period['status'], { tone: Tone; label: string }> = {
  open: { tone: 'ok', label: 'Open' },
  closed: { tone: 'warn', label: 'Closed' },
  locked: { tone: 'draft', label: 'Locked' },
};
const CHECK: Record<ChecklistItem['state'], { tone: Tone; label: string }> = {
  ok: { tone: 'ok', label: 'Done' },
  warning: { tone: 'warn', label: 'Check' },
  blocking: { tone: 'danger', label: 'Must fix' },
};

const monthName = (d: string) => new Intl.DateTimeFormat('en-IE', { month: 'long', timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));

export function YearEnd() {
  const { source } = useSession();
  const [version, setVersion] = useState(0);
  const years = useAsync(() => source.fiscalYears(), [source, version], { keep: true });
  const [selected, setSelected] = useState<string | null>(null);
  if (years.state === 'loading') return <PageHeader title="Year end and periods" subtitle="Loading…" />;
  if (years.state === 'error') return <><PageHeader title="Year end and periods" /><Banner>Couldn’t load: {years.error.message}</Banner></>;
  const year = years.data.find((y) => y.id === selected) ?? years.data.find((y) => y.status === 'open') ?? years.data[0];
  return (
    <>
      <PageHeader
        title="Year end and periods"
        subtitle="Close months as you finish with them; close the year when the accounts are agreed."
        actions={years.data.length > 1 ? (
          <select className="el-input" aria-label="Financial year" style={{ width: 'auto' }} value={year?.id} onChange={(e) => setSelected(e.target.value)}>
            {years.data.map((y) => <option key={y.id} value={y.id}>{y.starts_on.slice(0, 4)}{y.status === 'closed' ? ' (closed)' : ''}</option>)}
          </select>
        ) : undefined}
      />
      {year && <Year key={`${year.id}:${version}`} year={year} reload={() => setVersion((v) => v + 1)} />}
    </>
  );
}

function Year({ year, reload }: { year: FiscalYear; reload: () => void }) {
  const { source, can, today } = useSession();
  const toast = useToast((s) => s.show);
  const checks = useAsync(() => source.yearEndChecklist(year.id), [source, year.id]);
  const [dialog, setDialog] = useState<null | 'close' | 'reopen'>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = checks.state === 'ready' ? checks.data : [];
  const blocking = list.filter((c) => c.state === 'blocking');
  const warnings = list.filter((c) => c.state === 'warning');
  const ended = year.ends_on < today().toISOString().slice(0, 10);
  const mayClose = can('periods.close');

  async function run(work: () => Promise<unknown>, done: string) {
    setBusy(true);
    setError(null);
    try {
      await work();
      toast(done);
      setDialog(null);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const [closing, setClosing] = useState<Period | null>(null);
  function toggle(p: Period) {
    setError(null);
    if (p.status === 'open') setClosing(p);
    else void run(() => source.setPeriodStatus(p.id, 'open'), `${monthName(p.starts_on)} reopened`);
  }

  return (
    <div className="el-two-col" data-wide="right">
      <Card as="section" padding="18px 0 8px" aria-labelledby="months">
        <div className="el-card-head"><h2 id="months" className="el-h2">Months</h2><span className="el-hint">{year.status === 'closed' ? 'Year closed' : 'Closed months take no new entries'}</span></div>
        {error && !dialog && <div className="el-error-box" role="alert" style={{ margin: '0 16px 12px' }}>{error}</div>}
        <div className="el-table-wrap">
          <table className="el-table" aria-label="Months">
            <tbody>
              {year.periods.map((p) => (
                <tr key={p.id}>
                  <td>{monthName(p.starts_on)}</td>
                  <td><Pill tone={PERIOD[p.status].tone}>{PERIOD[p.status].label}</Pill>{p.locked_reason && <div className="el-hint">{p.locked_reason}</div>}</td>
                  <td className="el-num">
                    {p.status !== 'locked' && mayClose && (
                      <Button size="sm" variant="ghost" disabled={busy} onClick={() => toggle(p)}>{p.status === 'open' ? 'Close month' : 'Reopen'}</Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card as="section" padding="18px 20px" aria-labelledby="checklist">
        <div className="el-spread" style={{ marginBottom: 12 }}>
          <div>
            <h2 id="checklist" className="el-h2">Year end {year.ends_on.slice(0, 4)}</h2>
            <div className="el-hint">{longDate(year.starts_on)} to {longDate(year.ends_on)}</div>
          </div>
          <Pill tone={year.status === 'closed' ? 'draft' : 'ok'}>{year.status === 'closed' ? 'Closed' : 'Open'}</Pill>
        </div>
        {checks.state === 'loading' && <div className="el-skeleton" style={{ height: 120 }} aria-label="Loading checklist" />}
        <ul className="el-checklist" aria-label="Checklist">
          {list.map((c) => (
            <li key={c.key} data-state={c.state}>
              <Pill tone={CHECK[c.state].tone}>{CHECK[c.state].label}</Pill>
              <div><strong>{c.label}</strong>{c.detail && <div className="el-hint">{c.detail}</div>}</div>
            </li>
          ))}
        </ul>
        {year.status === 'open' ? (
          <>
            <p className="el-hint">
              {!ended ? `The year ends on ${longDate(year.ends_on)}. Work through the checklist as you go; you can close it after that.`
                : blocking.length ? 'Fix the items marked “Must fix” before closing.'
                  : 'Closing posts the year’s profit to retained earnings and locks every month. A backup is taken first. It can be reopened with a reason.'}
            </p>
            <div className="el-row" style={{ justifyContent: 'flex-end' }}>
              <Button variant="primary" disabled={!ended || blocking.length > 0 || !mayClose || busy} onClick={() => { setError(null); setDialog('close'); }}>Close the year</Button>
            </div>
          </>
        ) : (
          <div className="el-row" style={{ justifyContent: 'flex-end' }}>
            <Button variant="danger" disabled={!mayClose || busy} onClick={() => { setError(null); setReason(''); setDialog('reopen'); }}>Reopen the year</Button>
          </div>
        )}
      </Card>

      {closing && (
        <CloseMonth period={closing} onClose={() => setClosing(null)}
          onClosed={() => { toast(`${monthName(closing.starts_on)} closed`); setClosing(null); reload(); }} />
      )}
      {dialog === 'close' && (
        <Dialog title={`Close ${year.ends_on.slice(0, 4)}?`} onClose={() => setDialog(null)}
          actions={<><Button variant="ghost" onClick={() => setDialog(null)}>Not yet</Button>
            <Button variant="primary" disabled={busy} onClick={() => void run(() => source.closeYear(year.id, warnings.length > 0), `${year.ends_on.slice(0, 4)} closed`)}>
              {warnings.length ? 'Close anyway' : 'Close the year'}
            </Button></>}>
          {warnings.length > 0 && <p style={{ margin: 0 }}>Still to check: {warnings.map((w) => w.detail).join('; ')}.</p>}
          <p className="el-hint" style={{ margin: 0 }}>The closing journal moves this year’s profit to retained earnings. Every month is locked.</p>
          {error && <div className="el-error-box" role="alert">{error}</div>}
        </Dialog>
      )}
      {dialog === 'reopen' && (
        <Dialog title={`Reopen ${year.ends_on.slice(0, 4)}?`} onClose={() => setDialog(null)}
          actions={<><Button variant="ghost" onClick={() => setDialog(null)}>Keep it closed</Button>
            <Button variant="danger" disabled={busy || reason.trim().length < 3} onClick={() => void run(() => source.reopenYear(year.id, reason.trim()), `${year.ends_on.slice(0, 4)} reopened`)}>Reopen</Button></>}>
          <p style={{ margin: 0 }}>The closing journal is reversed and the months reopen, except any covered by a filed VAT return. Your accountant is told.</p>
          <Field id="reopen-reason" label="Reason">
            <input id="reopen-reason" className="el-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Accountant’s adjustments" />
          </Field>
          {error && <div className="el-error-box" role="alert">{error}</div>}
        </Dialog>
      )}
    </div>
  );
}

/** Closing a month: its checklist first; anything still open has to be confirmed. */
function CloseMonth({ period, onClose, onClosed }: { period: Period; onClose: () => void; onClosed: () => void }) {
  const { source } = useSession();
  const checks = useAsync(() => source.monthEndChecklist(period.id), [source, period.id]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = checks.state === 'ready' ? checks.data : [];
  const open = list.filter((c) => c.state !== 'ok');
  const month = `${monthName(period.starts_on)} ${period.starts_on.slice(0, 4)}`;

  async function close() {
    setBusy(true);
    setError(null);
    try {
      await source.closePeriod(period.id, open.length > 0);
      onClosed();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={`Close ${month}?`} onClose={onClose}
      actions={<><Button variant="ghost" onClick={onClose}>Not yet</Button>
        <Button variant="primary" disabled={busy || checks.state !== 'ready'} onClick={() => void close()}>
          {open.length ? 'Close anyway' : `Close ${monthName(period.starts_on)}`}
        </Button></>}>
      {checks.state === 'loading' && <div className="el-skeleton" style={{ height: 160 }} aria-label="Loading the month-end checklist" />}
      {checks.state === 'error' && <div className="el-error-box" role="alert">Couldn’t check the month: {checks.error.message}</div>}
      {checks.state === 'ready' && (
        <>
          <p style={{ margin: 0 }}>
            {open.length === 0 ? 'Everything for the month is done.'
              : open.length === 1 ? 'One thing is still open. You can close the month anyway, or deal with it first.'
                : `${open.length} things are still open. You can close the month anyway, or deal with them first.`}
          </p>
          <ul className="el-checklist" aria-label={`Month-end checklist for ${month}`}>
            {list.map((c) => (
              <li key={c.key} data-state={c.state}>
                <Pill tone={c.state === 'ok' ? 'ok' : 'warn'}>{c.state === 'ok' ? 'Done' : 'Open'}</Pill>
                <div><strong>{c.label}</strong>{c.detail && <div className="el-hint">{c.detail}</div>}</div>
              </li>
            ))}
          </ul>
          <p className="el-hint" style={{ margin: 0 }}>A closed month takes no new entries. It can be reopened until a VAT return locks it.</p>
        </>
      )}
      {error && <div className="el-error-box" role="alert">{error}</div>}
    </Dialog>
  );
}
